import type {
  SymbolLayerSpecification,
  LineLayerSpecification,
  FillLayerSpecification,
  FilterSpecification,
  ExpressionSpecification,
} from 'mapbox-gl'
import { HATCH_IMAGE_PREFIX } from './hatchPattern'

// Twin: mobile renders the same tilesets with its own copy of these style
// definitions (camelCase rnmapbox props) in
// mobile/app/components/atomic/elements/geolocation/utilities/dxfLayerStyles.ts
// — when changing an expression here, change it there too.

// Mapbox GL clamps `text-size` to ~255px — beyond it glyphs distort or labels
// are dropped entirely. Ground-scaled DXF text blows past this when zoomed in
// (a 50m-tall label hits 255px around zoom ~18), so we cap it here; above the
// cap the text holds steady instead of growing unbounded and vanishing.
const MAX_TEXT_SIZE = 255

// Web-Mercator ground resolution at zoom 0 on the equator (metres per pixel).
const EQUATOR_METRES_PER_PIXEL = 156543.03392

// DXF lineweights arrive in drawing units; this scale maps them to sensible
// screen-pixel widths (chosen visually against the Sizewell drawings).
const DXF_LINEWEIGHT_TO_PIXELS = 4

// Fallback colour when the converter didn't tag a feature with one.
const DEFAULT_DXF_COLOR = '#ff0000'

const DEFAULT_LINE_WIDTH = 2
const DEFAULT_OUTLINE_WIDTH = 1.5

const SUFFIX_POLYGON_HATCH = '-polygon-hatch'
const SUFFIX_POLYGON_FILL = '-polygon-fill'
const SUFFIX_POLYGON_OUTLINE = '-polygon-outline'
const SUFFIX_LINE = '-line'
const SUFFIX_POINT = '-point'
const SUFFIX_LABEL = '-label'

// Style-layer id suffixes createDXFMapboxLayer produces, in the z-order the
// layers are added (bottom first). Consumers derive removal lists and
// z-anchoring from this — never hardcode the suffixes elsewhere.
export const DXF_STYLE_LAYER_SUFFIXES_BOTTOM_TO_TOP = [
  SUFFIX_POLYGON_HATCH,
  SUFFIX_POLYGON_FILL,
  SUFFIX_POLYGON_OUTLINE,
  SUFFIX_LINE,
  SUFFIX_POINT,
  SUFFIX_LABEL,
]

export const createDXFMapboxLayer = ({
  id,
  sourceLayer,
  centerLat,
}: {
  id: string
  sourceLayer: string
  centerLat: number
}) => {
  // DXF text is rendered at true ground scale: a glyph of `height` drawing-units
  // (metres) occupies that many metres on the map, growing with zoom like a CAD
  // viewer. The cos(lat) term accounts for Web-Mercator's latitude distortion;
  // `centerLat` is the tileset's centre latitude, accurate enough across a view.
  const groundConstant =
    EQUATOR_METRES_PER_PIXEL * Math.cos((centerLat * Math.PI) / 180)

  // Mapbox only permits the `zoom` expression as the input to an `interpolate`
  // (it cannot be wrapped in `min`), but the cap can live inside each stop's
  // value, which depends only on `height`. Base-2 interpolation between any two
  // stops on the `2^zoom` curve reproduces it exactly, so emitting a stop per
  // integer zoom tracks true ground scale below the ceiling and holds flat above.
  const dxfTextSize: ExpressionSpecification = [
    'interpolate',
    ['exponential', 2],
    ['zoom'],
    ...Array.from({ length: 25 }, (_, zoom) => [
      zoom,
      [
        'min',
        MAX_TEXT_SIZE,
        ['*', ['get', 'height'], 2 ** zoom / groundConstant],
      ],
    ]).flat(),
  ] as ExpressionSpecification

  const pointFilter = [
    'all',
    ['==', ['geometry-type'], 'Point'],
    ['!', ['in', ['get', 'type'], ['literal', ['TEXT', 'MTEXT']]]],
  ]

  const labelFilter = [
    'all',
    ['in', ['get', 'type'], ['literal', ['TEXT', 'MTEXT']]],
  ]

  const pointLayer: SymbolLayerSpecification = {
    id: `${id}${SUFFIX_POINT}`,
    type: 'symbol',
    'source-layer': sourceLayer,
    source: id,
    layout: {
      'icon-image': 'dot-11',
      'text-field': ['get', 'name'],
      'text-size': 14,
      'text-anchor': 'center',
      'text-offset': [0, -1],
    },
    paint: {
      'text-color': '#ffffff',
      'text-halo-color': '#000000',
      'text-halo-width': 1,
    },
    filter: pointFilter,
  }

  const labelLayer: SymbolLayerSpecification = {
    id: `${id}${SUFFIX_LABEL}`,
    type: 'symbol',
    source: id,
    'source-layer': sourceLayer,
    layout: {
      'text-field': ['get', 'text'],
      'text-size': dxfTextSize,
      'text-rotate': ['*', -1, ['get', 'rotation']],
      'text-rotation-alignment': 'map',
      'text-anchor': ['coalesce', ['get', 'anchor'], 'center'],
      'text-allow-overlap': true,
      'text-max-width': [
        'case',
        ['has', 'width'],
        ['/', ['get', 'width'], ['get', 'height']],
        50,
      ],
    },
    paint: {
      'text-color': ['coalesce', ['get', 'color'], DEFAULT_DXF_COLOR],
      'text-halo-color': '#fff',
      'text-halo-width': 1,
    },
    filter: labelFilter,
  }

  const lineLayer: LineLayerSpecification = {
    id: `${id}${SUFFIX_LINE}`,
    type: 'line',
    source: id,
    'source-layer': sourceLayer,
    paint: {
      'line-color': ['coalesce', ['get', 'color'], DEFAULT_DXF_COLOR],
      'line-width': [
        'case',
        ['has', 'lineweight'],
        ['*', ['get', 'lineweight'], DXF_LINEWEIGHT_TO_PIXELS],
        DEFAULT_LINE_WIDTH,
      ],
    },
    filter: ['all', ['==', ['geometry-type'], 'LineString']],
  }

  const polygonFilter = [
    'all',
    ['in', ['geometry-type'], ['literal', ['Polygon', 'MultiPolygon']]],
  ]

  // Only solid hatches (tagged `fill: true` by the converter) get a solid fill.
  const polygonFillFilter = [
    'all',
    ['in', ['geometry-type'], ['literal', ['Polygon', 'MultiPolygon']]],
    ['==', ['get', 'fill'], true],
  ] as FilterSpecification

  // Pattern (non-solid) hatches (tagged `hatch: true`) get a diagonal hatch fill.
  const polygonHatchFilter = [
    'all',
    ['in', ['geometry-type'], ['literal', ['Polygon', 'MultiPolygon']]],
    ['==', ['get', 'hatch'], true],
  ] as FilterSpecification

  // Rendered beneath the solid fill so solid areas (e.g. vegetation) draw over
  // the hatch, as in a CAD viewer. Hatch images are generated per colour on the
  // map's `styleimagemissing` event (see registerHatchImageHandler).
  const polygonLayerHatch: FillLayerSpecification = {
    id: `${id}${SUFFIX_POLYGON_HATCH}`,
    type: 'fill',
    source: id,
    'source-layer': sourceLayer,
    paint: {
      'fill-pattern': [
        'image',
        [
          'concat',
          HATCH_IMAGE_PREFIX,
          ['coalesce', ['get', 'color'], DEFAULT_DXF_COLOR],
        ],
      ],
      'fill-opacity': 0.4,
    },
    filter: polygonHatchFilter,
  }

  const polygonLayerFill: FillLayerSpecification = {
    id: `${id}${SUFFIX_POLYGON_FILL}`,
    type: 'fill',
    source: id,
    'source-layer': sourceLayer,
    paint: {
      'fill-color': ['coalesce', ['get', 'color'], DEFAULT_DXF_COLOR],
      'fill-opacity': 0.3,
    },
    filter: polygonFillFilter,
  }

  const polygonLayerOutline: LineLayerSpecification = {
    id: `${id}${SUFFIX_POLYGON_OUTLINE}`,
    type: 'line',
    source: id,
    'source-layer': sourceLayer,
    paint: {
      'line-color': ['coalesce', ['get', 'color'], DEFAULT_DXF_COLOR],
      'line-width': [
        'case',
        ['has', 'lineweight'],
        ['*', ['get', 'lineweight'], DXF_LINEWEIGHT_TO_PIXELS],
        DEFAULT_OUTLINE_WIDTH,
      ],
    },
    filter: polygonFilter,
  }

  // Bottom-to-top, matching DXF_STYLE_LAYER_SUFFIXES_BOTTOM_TO_TOP: the hatch
  // sits below the solid fill, then outlines, lines, points and labels on top.
  return [
    polygonLayerHatch,
    polygonLayerFill,
    polygonLayerOutline,
    lineLayer,
    pointLayer,
    labelLayer,
  ]
}
